import { Fragment, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useScratch from 'react-use/lib/useScratch';
import useToggle from 'react-use/lib/useToggle';
import { Box, Button, Flex, Grid, SxStyleProp, Text } from 'theme-ui';
import { clamp } from 'ramda';

import { BORDER_WIDTH, HANDLE_WIDTH, Range, RangeProps } from 'shared/Range';
import Modal from 'shared/Modal';
import { TimeScale } from 'shared/TimeScale';
import { formatAudioTime, msToTimeFormat } from 'utils/formatTime';
import TimeChooser from '../../assets/time-chooser.svg';
import audioWave from '../../assets/image/audioWave.png';
import { throttle } from 'throttle-debounce';
import Icon, { IconTypes } from 'shared/Icon';
import { CONTENT_TEAM_MODE } from 'utils/env';
import { useTranslator } from '@picsart/rc/services/localization';
import { usePlayer } from 'utils/PlayerContext';

type AudioTrimModalProps = {
  opened: boolean;
  close: () => void;
  src: string;
  title: string;
  trimDuration: number;
  trimStart: number;
  applyHandler: (start: number, duration: number) => void;
  duration: number;
};

const rangeAudioStyle = (backgroundPosition: 'left' | 'right'): SxStyleProp => ({
  '.handleContainer': {
    backgroundImage: `url(${audioWave})`,
    backgroundRepeat: 'repeat-x',
    backgroundPosition
  }
});

export const AudioTrimModal: FC<AudioTrimModalProps> = ({
  opened,
  close,
  src,
  title,
  trimDuration,
  trimStart = 0,
  duration,
  applyHandler: applyFn
}) => {
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const audioRef = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [paused, togglePaused] = useToggle(true);
  const bgPosition = useRef<'left' | 'right'>('left');
  const timestamp = useRef<string>('');
  const translator = useTranslator();
  const [timeSliderRef, timeSliderState] = useScratch();
  const { pause } = usePlayer();

  useEffect(() => {
    if (!CONTENT_TEAM_MODE) return;
    timestamp.current = msToTimeFormat(currentTime * 1000, true);
  }, [currentTime]);

  const throttledSetVideoTime = useCallback(
    throttle(200, (time: number) => {
      audioRef.current.currentTime = time;
    }),
    [audioRef.current]
  );

  useEffect(() => {
    if (!audioRef.current) return;
    if (timeSliderState.isScratching) {
      const val = Math.fround(
        clamp(
          start,
          end,
          ((timeSliderState.x + timeSliderState.dx) / timeSliderState.elW) * duration
        ) / 1000
      );
      if (!Number.isNaN(val)) {
        setCurrentTime(val);
        throttledSetVideoTime(val);
      }
    }
  }, [timeSliderState.dx, duration]);

  useEffect(() => {
    if (!opened) {
      togglePaused(true);
    }
    if (!audioRef.current) return;
    setStart(trimStart);
    setEnd(trimDuration ? trimDuration + trimStart : duration);
    setCurrentTime(trimStart / 1000);
    audioRef.current.currentTime = trimStart / 1000;
    audioRef.current.volume = 0.5;
  }, [opened]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (currentTime * 1000 <= start) {
      setCurrentTime(start / 1000);
      throttledSetVideoTime(start / 1000);
    }
    bgPosition.current = 'right';
  }, [start]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (currentTime * 1000 >= end) {
      setCurrentTime(end / 1000);
      throttledSetVideoTime(end / 1000);
    }
    bgPosition.current = 'left';
  }, [end]);

  const displayDuration = useMemo(() => formatAudioTime((end - start) / 1000), [end, start]);

  const trimHandler = useCallback<RangeProps['onChange']>((startVal, endVal) => {
    setStart(startVal);
    setEnd(endVal);
  }, []);

  const applyHandler = () => {
    applyFn(start, end - start);
    close();
  };

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (audioRef.current.paused) {
      if (currentTime * 1000 > end || currentTime * 1000 < start) {
        audioRef.current.currentTime = start / 1000;
      }

      pause();
      audioRef.current.play();
      togglePaused(false);
    } else {
      audioRef.current.pause();
      togglePaused(true);
    }
  };

  const audioEndHandler = () => {
    togglePaused(true);
    setCurrentTime(start / 1000);
    throttledSetVideoTime(start / 1000);
  };

  const audioTimeUpdateHandler = (e: any) => {
    const { currentTime: time, paused: audioPaused } = e.target;

    if (!audioPaused) {
      if (time * 1000 > end) {
        audioRef.current.pause();
        audioEndHandler();
      } else {
        setCurrentTime(time);
      }
    }
  };
  return (
    <Fragment>
      <Modal
        color="transparent"
        opened={opened}
        closeHandler={close}
        title={translator('Trim_Tool')}
      >
        <Box sx={{ width: 756 }}>
          <audio
            src={src}
            muted={false}
            ref={audioRef}
            onTimeUpdate={audioTimeUpdateHandler}
            onEnded={audioEndHandler}
          />
          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
            <Icon name="audio-editor" />
            <Text variant="body" sx={{ fontSize: 2, mt: 5 }}>
              {title}
            </Text>
          </Flex>
          {CONTENT_TEAM_MODE && (
            <Box sx={{ my: 4 }}>
              <Text variant="modalBody" sx={{ fontFamily: 'monospace' }}>
                {timestamp.current}
              </Text>
            </Box>
          )}
          <Grid columns={[3, '64px auto 64px']} gap={5} sx={{ mt: 7, userSelect: 'none' }}>
            <Button
              mr={3}
              sx={{ width: 64, height: 40, py: 0, justifyContent: 'center' }}
              onClick={togglePlay}
              variant="outlined"
            >
              <Icon name={paused ? 'play-editor' : 'pause-editor'} type={IconTypes.Primary} />
            </Button>
            <Box sx={{ position: 'relative', mt: 2 }}>
              <Range
                max={duration}
                value1={start}
                value2={end}
                onChange={trimHandler}
                sx={rangeAudioStyle(bgPosition.current)}
              />
              <TimeScale min={0} max={duration / 1000} pointsCount={5} formatFn={formatAudioTime} />
              <div
                data-test="trim-time_slider"
                ref={timeSliderRef}
                sx={{
                  height: 0,
                  left: HANDLE_WIDTH + BORDER_WIDTH,
                  top: 0,
                  width: `calc(100% - ${(HANDLE_WIDTH + BORDER_WIDTH) * 2}px)`,
                  position: 'absolute'
                }}
              >
                <div
                  sx={{
                    position: 'absolute',
                    width: 0,
                    transform: 'translateX(-5px)',
                    height: '34px',
                    top: '-14px'
                  }}
                  style={{
                    left: `${clamp(0, 1, (currentTime * 1000) / duration) * 100}%`
                  }}
                >
                  <TimeChooser />
                  <div sx={{ width: '12px', height: '100%', ml: '-6px' }} />
                </div>
              </div>
            </Box>
            <Flex
              sx={{
                bg: 'grey4',
                px: 4,
                borderRadius: '20px',
                alignItems: 'center',
                height: 24,
                mt: 2
              }}
            >
              <Text variant="body" sx={{ fontSize: 2, color: 'background', mb: 0 }}>
                {displayDuration}
              </Text>
            </Flex>
          </Grid>
          <Flex sx={{ mt: 8, justifyContent: 'flex-end' }}>
            <Button variant="primary" onClick={applyHandler} sx={{ px: 6, py: 4 }}>
              {translator('Apply')}
            </Button>
          </Flex>
        </Box>
      </Modal>
    </Fragment>
  );
};
